import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Sparkles, Crown, Lock, Clock, Lightbulb, AlertCircle, ScanFace, Droplet, ExternalLink, TrendingUp, ArrowUp, ArrowDown, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import ScanOverlay from './ScanOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ATTR_META = {
  acne: 'Acne', blackheads: 'Blackheads', whiteheads: 'Whiteheads', cysts: 'Cysts & Nodules', congestion: 'Congestion',
  texture: 'Texture', pores: 'Pores', oiliness: 'Oiliness', roughness: 'Roughness', acne_scars: 'Acne Scars',
  dark_spots: 'Dark Spots', uneven_tone: 'Uneven Tone', sun_damage: 'Sun Damage', melasma: 'Melasma', radiance: 'Radiance',
  hydration: 'Hydration', dryness: 'Dryness', flakiness: 'Flakiness', barrier_strength: 'Barrier Strength', dullness: 'Dullness',
  wrinkles: 'Wrinkles', fine_lines: 'Fine Lines', firmness: 'Firmness', elasticity: 'Elasticity', sagging: 'Sagging',
  redness: 'Redness', irritation: 'Irritation', dark_circles: 'Dark Circles', eye_bags: 'Eye Bags', puffiness: 'Puffiness',
};

const DEFAULT_GROUPS = {
  'Acne & Congestion': ['acne', 'blackheads', 'whiteheads', 'cysts', 'congestion'],
  'Texture & Pores': ['texture', 'pores', 'oiliness', 'roughness', 'acne_scars'],
  'Pigmentation & Tone': ['dark_spots', 'uneven_tone', 'sun_damage', 'melasma', 'radiance'],
  'Hydration & Barrier': ['hydration', 'dryness', 'flakiness', 'barrier_strength', 'dullness'],
  'Aging & Elasticity': ['wrinkles', 'fine_lines', 'firmness', 'elasticity', 'sagging'],
  'Redness & Eye Area': ['redness', 'irritation', 'dark_circles', 'eye_bags', 'puffiness'],
};

const DEFAULT_SCALES = {
  'Acne & Congestion': 'Hayashi · IGA Scale',
  'Texture & Pores': 'SCINEXA Index',
  'Pigmentation & Tone': 'Fitzpatrick · MASI',
  'Hydration & Barrier': 'Corneometry · TEWL',
  'Aging & Elasticity': 'Glogau Scale',
  'Redness & Eye Area': 'CEA Grading',
};

const scoreColor = (s) => (s >= 75 ? '#FF6B4A' : s >= 50 ? '#C99A2E' : '#A388EE');
const scoreLabel = (s) => (s >= 75 ? 'Great' : s >= 50 ? 'Fair' : 'Needs care');
const SEVERITY = {
  mild: { color: '#C99A2E', bg: '#C99A2E' },
  moderate: { color: '#A388EE', bg: '#A388EE' },
  severe: { color: '#FF3B30', bg: '#FF3B30' },
};

const ScoreRing = ({ score, size = 132, stroke = 12, dark = false }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, score)) / 100) * c;
  const color = dark ? (score >= 75 ? '#4ADE80' : score >= 50 ? '#E2C56B' : '#E2A593') : scoreColor(score);
  const track = dark ? 'rgba(255,255,255,0.18)' : '#F3E6E0';
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }} data-testid="skin-overall-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.9s ease-out' }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-heading text-4xl font-bold" style={{ color: dark ? '#fff' : color }}>{score}</span>
        <span className={`text-xs uppercase tracking-wider ${dark ? 'text-white/60' : 'text-[#8C7A76]'}`}>/ 100</span>
      </div>
    </div>
  );
};

const DeltaBadge = ({ delta, size = 'sm' }) => {
  if (delta == null || delta === 0) return null;
  const up = delta > 0;
  const cls = size === 'lg' ? 'text-xs px-2 py-0.5' : 'text-[10px] px-1.5 py-0.5';
  return (
    <span className={`inline-flex items-center gap-0.5 font-bold rounded-full ${cls} ${up ? 'bg-[#FF6B4A]/12 text-[#FF6B4A]' : 'bg-[#A388EE]/12 text-[#A388EE]'}`}>
      {up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{Math.abs(delta)}
    </span>
  );
};

const AttributeBar = ({ attrKey, score, delta }) => {
  const color = scoreColor(score);
  return (
    <div data-testid={`skin-attr-${attrKey}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-[#1A1514]">{ATTR_META[attrKey] || attrKey}</span>
        <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color }}>
          <DeltaBadge delta={delta} />{score} · {scoreLabel(score)}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-[#F3E6E0] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: color, transition: 'width 0.9s ease-out' }} />
      </div>
    </div>
  );
};

const Badge = ({ icon: Icon, label, value, testid }) => (
  <div className="inline-flex items-center gap-2 bg-[#FFF2EC] rounded-xl px-3 py-2" data-testid={testid}>
    {Icon && <Icon className="w-4 h-4 text-[#FF6B4A]" />}
    <div className="leading-tight">
      <span className="block text-sm font-bold text-[#1A1514] capitalize">{value}</span>
      <span className="block text-[10px] text-[#8C7A76] uppercase tracking-wider">{label}</span>
    </div>
  </div>
);

const trackClick = (productId) => {
  axios.post(`${API}/affiliate/click`, { product_id: productId }, { withCredentials: true }).catch(() => {});
};

const stripMd = (t) => (t || '').replace(/\*\*/g, '').replace(/^[-•]\s*/gm, '').trim();

const SEV_FREE = {
  none: { label: 'Clear', color: '#FF6B4A', bg: '#4ADE80' },
  mild: { label: 'Mild', color: '#fff', bg: '#C99A2E' },
  moderate: { label: 'Moderate', color: '#fff', bg: '#A388EE' },
  severe: { label: 'Severe', color: '#fff', bg: '#FF3B30' },
};

const LockedReport = ({ analysis, onUpgrade }) => {
  const hasStructured = analysis?.health_score != null;
  const score100 = hasStructured ? analysis.health_score * 10 : null;
  const sev = SEV_FREE[analysis?.severity] || SEV_FREE.none;

  return (
    <Card className="card-elevated border-0 overflow-hidden" data-testid="skin-report-locked">
      <CardContent className="p-0">
        {/* Pretty free snapshot */}
        <div className="bg-gradient-to-br from-[#FF6B4A] to-[#E8552F] text-white p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#4ADE80]" />
            <span className="text-xs font-semibold uppercase tracking-wider text-[#4ADE80]">Quick Skin Snapshot</span>
          </div>

          {hasStructured ? (
            <div className="flex items-center gap-5">
              <ScoreRing score={score100} size={104} stroke={10} dark />
              <div className="flex-1 space-y-2">
                <div>
                  <p className="text-xs text-white/60 uppercase tracking-wider">Skin Health</p>
                  <p className="font-heading text-2xl font-bold">{analysis.health_score}<span className="text-base text-white/60">/10</span></p>
                </div>
                <span className="inline-block text-[11px] font-bold uppercase px-3 py-1 rounded-full"
                  style={{ backgroundColor: sev.bg, color: sev.color }} data-testid="free-severity">
                  {sev.label} acne
                </span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">{stripMd(analysis?.raw_analysis)}</p>
          )}
        </div>

        {hasStructured && (analysis.highlight || analysis.tip) && (
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 border-b border-[#F3E6E0]">
            {analysis.highlight && (
              <div className="rounded-xl bg-[#4ADE80]/12 p-4" data-testid="free-highlight">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Sparkles className="w-4 h-4 text-[#FF6B4A]" />
                  <span className="text-xs font-semibold text-[#FF6B4A] uppercase tracking-wider">Looking great</span>
                </div>
                <p className="text-sm text-[#1A1514] leading-relaxed">{analysis.highlight}</p>
              </div>
            )}
            {analysis.tip && (
              <div className="rounded-xl bg-[#A388EE]/10 p-4" data-testid="free-tip">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Lightbulb className="w-4 h-4 text-[#A388EE]" />
                  <span className="text-xs font-semibold text-[#A388EE] uppercase tracking-wider">Your quick tip</span>
                </div>
                <p className="text-sm text-[#1A1514] leading-relaxed">{analysis.tip}</p>
              </div>
            )}
          </div>
        )}

        {/* Premium teaser */}
        <div className="relative p-6">
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 blur-sm select-none pointer-events-none" aria-hidden="true">
            {['acne', 'redness', 'pores', 'hydration', 'dark_spots', 'wrinkles', 'texture', 'dark_circles'].map((k, i) => (
              <AttributeBar key={k} attrKey={k} score={[82, 64, 71, 58, 77, 49, 68, 85][i]} />
            ))}
          </div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-white/40 backdrop-blur-[1px]">
            <div className="w-12 h-12 rounded-full bg-[#FF6B4A] flex items-center justify-center mb-3">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <p className="font-heading text-lg font-bold text-[#1A1514]">Unlock your 30-Point Clinical Diagnostic</p>
            <p className="text-sm text-[#8C7A76] mt-1 mb-4 max-w-xs">
              30 clinically-graded skin points mapped on your photo, zone heat map, detected issues, skin age & a personalized action plan with Premium.
            </p>
            <button onClick={onUpgrade}
              className="inline-flex items-center gap-1.5 bg-[#FF6B4A] hover:bg-[#E8552F] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
              data-testid="skin-report-upgrade-btn">
              <Crown className="w-4 h-4" /> Upgrade to Premium
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SkinReportCard = ({ selfie, prevAnalysis, onUpgrade, imageSrc }) => {
  const analysis = selfie?.analysis;
  if (!analysis) return null;

  const dateStr = selfie.created_at
    ? new Date(selfie.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  if (!analysis.scores || analysis.is_premium === false) {
    return <LockedReport analysis={analysis} onUpgrade={onUpgrade} />;
  }

  const {
    overall_score = 0, skin_age, skin_type, percentile,
    scores = {}, groups, clinical_scales, zone_scores, detected_issues = [], top_concerns = [], face_box,
    summary, recommendation, recommended_products = [],
  } = analysis;

  const groupDefs = groups && Object.keys(groups).length ? groups : DEFAULT_GROUPS;
  const scaleDefs = clinical_scales && Object.keys(clinical_scales).length ? clinical_scales : DEFAULT_SCALES;
  const pointCount = Object.keys(scores).length;

  // Before/after deltas vs the previous scan (higher score = better)
  const prevScores = prevAnalysis?.scores || null;
  const deltaFor = (k) => (prevScores && prevScores[k] != null ? scores[k] - prevScores[k] : null);
  const overallDelta = prevAnalysis?.overall_score != null ? overall_score - prevAnalysis.overall_score : null;
  const movers = Object.keys(ATTR_META)
    .filter((k) => k in scores && deltaFor(k) != null && deltaFor(k) !== 0)
    .map((k) => ({ k, d: deltaFor(k) }))
    .sort((a, b) => Math.abs(b.d) - Math.abs(a.d));
  const improvements = movers.filter((m) => m.d > 0).slice(0, 3);
  const declines = movers.filter((m) => m.d < 0).slice(0, 2);

  return (
    <Card className="card-elevated border-0 overflow-hidden" data-testid="skin-report-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ScanFace className="w-5 h-5 text-[#FF6B4A]" />
            <h3 className="font-heading text-xl font-bold text-[#1A1514]">AI Skin Diagnostic</h3>
          </div>
          {dateStr && (
            <span className="flex items-center gap-1 text-xs text-[#8C7A76]"><Clock className="w-3 h-3" /> {dateStr}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#FF6B4A] text-white px-2.5 py-1 rounded-full" data-testid="clinical-points-badge">
            <Sparkles className="w-3 h-3" /> {pointCount >= 30 ? '30' : pointCount}-Point Clinical Analysis
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-[#4ADE80]/15 text-[#FF6B4A] px-2.5 py-1 rounded-full" data-testid="clinical-graded-badge">
            <ShieldCheck className="w-3 h-3" /> Dermatology-grade scales
          </span>
        </div>

        {/* On-photo zone analysis */}
        {imageSrc && zone_scores && (
          <ScanOverlay imageSrc={imageSrc} zoneScores={zone_scores} scores={scores} detectedIssues={detected_issues} attrMeta={ATTR_META} faceBox={face_box} />
        )}

        {/* Overall + badges */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-6">
          <ScoreRing score={overall_score} />
          <div className="flex-1 w-full space-y-3">
            <div>
              <p className="text-xs text-[#8C7A76] uppercase tracking-wider">Overall Skin Health</p>
              <p className="flex items-center gap-2 font-semibold" style={{ color: scoreColor(overall_score) }}>
                {scoreLabel(overall_score)}
                {overallDelta != null && <span data-testid="overall-delta"><DeltaBadge delta={overallDelta} size="lg" /></span>}
                {overallDelta != null && <span className="text-xs font-normal text-[#8C7A76]">vs last scan</span>}
              </p>
            </div>
            {percentile != null && (
              <div className="flex items-center gap-1.5 text-sm text-[#FF6B4A]" data-testid="skin-percentile">
                <TrendingUp className="w-4 h-4" />
                <span>Healthier than <strong>{percentile}%</strong> of people your age</span>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {skin_age ? <Badge icon={Clock} label="Skin Age" value={`${skin_age} yrs`} testid="skin-age" /> : null}
              {skin_type && <Badge icon={Droplet} label="Skin Type" value={skin_type} testid="skin-type" />}
            </div>
          </div>
        </div>

        {/* Progress since last scan */}
        {(improvements.length > 0 || declines.length > 0) && (
          <div className="mb-6 rounded-xl bg-[#FFF2EC] p-4" data-testid="progress-since-last">
            <div className="flex items-center gap-1.5 mb-3">
              <TrendingUp className="w-4 h-4 text-[#FF6B4A]" />
              <span className="text-xs font-semibold text-[#1A1514] uppercase tracking-wider">Progress since last scan</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {improvements.map((m) => (
                <span key={m.k} className="inline-flex items-center gap-1 text-xs font-semibold bg-[#FF6B4A]/10 text-[#FF6B4A] px-3 py-1.5 rounded-full">
                  <ArrowUp className="w-3 h-3" />{ATTR_META[m.k]} +{m.d}
                </span>
              ))}
              {declines.map((m) => (
                <span key={m.k} className="inline-flex items-center gap-1 text-xs font-semibold bg-[#A388EE]/10 text-[#A388EE] px-3 py-1.5 rounded-full">
                  <ArrowDown className="w-3 h-3" />{ATTR_META[m.k]} {m.d}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Detected Issues — the competitive edge */}
        {detected_issues.length > 0 && (
          <div className="mb-6 rounded-xl border border-[#F3E6E0] overflow-hidden" data-testid="detected-issues">
            <div className="flex items-center gap-2 px-4 py-3 bg-[#FFF2EC] border-b border-[#F3E6E0]">
              <AlertCircle className="w-4 h-4 text-[#A388EE]" />
              <span className="text-sm font-semibold text-[#1A1514]">Detected Issues ({detected_issues.length})</span>
            </div>
            <div className="divide-y divide-[#F3E6E0]">
              {detected_issues.map((it, idx) => {
                const sev = SEVERITY[it.severity] || SEVERITY.mild;
                return (
                  <div key={idx} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`detected-issue-${idx}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#1A1514] truncate">{it.issue}</span>
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white flex-shrink-0"
                          style={{ backgroundColor: sev.bg }}>{it.severity}</span>
                      </div>
                      {it.zones?.length > 0 && (
                        <p className="text-xs text-[#8C7A76] mt-0.5 capitalize">{it.zones.join(' · ')}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Top concerns */}
        {top_concerns.length > 0 && (
          <div className="mb-6">
            <span className="text-xs font-semibold text-[#8C7A76] uppercase tracking-wider">Top Concerns</span>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="skin-top-concerns">
              {top_concerns.map((c) => (
                <span key={c} className="text-xs font-semibold bg-[#A388EE]/10 text-[#A388EE] px-3 py-1 rounded-full">
                  {ATTR_META[c] || c}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Grouped attribute scores */}
        <div className="space-y-5 mb-6">
          {Object.entries(groupDefs).map(([groupName, keys]) => {
            const presentKeys = keys.filter((k) => k in scores);
            if (presentKeys.length === 0) return null;
            return (
              <div key={groupName} data-testid={`skin-group-${groupName.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-[#FF6B4A] uppercase tracking-wider">{groupName}</p>
                  {scaleDefs[groupName] && (
                    <span className="text-[10px] text-[#8C7A76] bg-[#FFF2EC] border border-[#F3E6E0] px-2 py-0.5 rounded-full">{scaleDefs[groupName]}</span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  {presentKeys.map((k) => <AttributeBar key={k} attrKey={k} score={scores[k]} delta={deltaFor(k)} />)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        {summary && <p className="text-sm text-[#1A1514] leading-relaxed mb-4">{summary}</p>}

        {/* Recommendation */}
        {recommendation && (
          <div className="bg-gradient-to-r from-[#4ADE80]/15 to-[#FF6B4A]/5 rounded-xl p-4 flex gap-3 mb-6" data-testid="skin-recommendation">
            <Lightbulb className="w-5 h-5 text-[#FF6B4A] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-[#FF6B4A] uppercase tracking-wider mb-1">Recommended Next Step</p>
              <p className="text-sm text-[#1A1514] leading-relaxed">{recommendation}</p>
            </div>
          </div>
        )}

        {/* Matched products */}
        {recommended_products.length > 0 && (
          <div data-testid="skin-matched-products">
            <p className="text-xs font-semibold text-[#8C7A76] uppercase tracking-wider mb-3">Products for your concerns</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {recommended_products.map((p) => (
                <a key={p.product_id} href={p.affiliate_url} target="_blank" rel="noopener noreferrer"
                  onClick={() => trackClick(p.product_id)}
                  className="flex items-center gap-3 bg-[#FFF2EC] hover:bg-[#F3E6E0] rounded-xl p-3 transition-colors group"
                  data-testid={`matched-product-${p.product_id}`}>
                  <img src={p.image} alt={p.name} className="w-12 h-12 rounded-lg object-cover bg-white flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold text-[#4ADE80] uppercase tracking-wider">For {ATTR_META[p.concern] || p.concern}</p>
                    <p className="text-sm font-medium text-[#1A1514] truncate">{p.name}</p>
                    <p className="text-xs text-[#FF6B4A] font-semibold flex items-center gap-1">
                      {p.price} <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SkinReportCard;
